export type HealthSourceId =
  | "nhs-child-medicines"
  | "gosh-medicine-safety"
  | "who-vaccination"
  | "cdc-infection-control"
  | "cdc-one-health"
  | "fao-plant-health"
  | "woah-animal-vaccination";

export interface HealthSource {
  id: HealthSourceId;
  organisation: string;
  title: string;
  url: string;
}

export const healthSources: HealthSource[] = [
  {
    id: "nhs-child-medicines",
    organisation: "NHS",
    title: "Medicines for babies and children",
    url: "https://www.nhs.uk/baby/health/medicines-for-babies-and-children/",
  },
  {
    id: "gosh-medicine-safety",
    organisation: "Great Ormond Street Hospital",
    title: "Making the most of your child's medicines",
    url: "https://www.gosh.nhs.uk/conditions-and-treatments/medicines-information/medicines-advice/making-most-your-child-s-medicines/",
  },
  {
    id: "who-vaccination",
    organisation: "World Health Organization",
    title: "Vaccines and immunization: What is vaccination?",
    url: "https://www.who.int/news-room/questions-and-answers/item/vaccines-and-immunization-what-is-vaccination",
  },
  {
    id: "cdc-infection-control",
    organisation: "US Centers for Disease Control and Prevention",
    title: "Infection Control Basics",
    url: "https://www.cdc.gov/infection-control/about/index.html",
  },
  {
    id: "cdc-one-health",
    organisation: "US Centers for Disease Control and Prevention",
    title: "About Zoonotic Diseases",
    url: "https://www.cdc.gov/one-health/about/about-zoonotic-diseases.html",
  },
  {
    id: "fao-plant-health",
    organisation: "Food and Agriculture Organization of the United Nations",
    title: "Plant health",
    url: "https://www.fao.org/one-health/areas-of-work/plant-health",
  },
  {
    id: "woah-animal-vaccination",
    organisation: "World Organisation for Animal Health",
    title: "Vaccine banks",
    url: "https://www.woah.org/en/what-we-offer/improving-veterinary-services/vaccine-banks/",
  },
];

export interface SourcedHealthClaim {
  id: string;
  text: string;
  sourceIds: HealthSourceId[];
}

export const healthClaims: SourcedHealthClaim[] = [
  {
    id: "medicine-purpose",
    text: "Medicines can help treat illness, and some medicines help prevent illness. A responsible adult or health professional decides what is suitable.",
    sourceIds: ["nhs-child-medicines", "who-vaccination"],
  },
  {
    id: "medicine-forms",
    text: "Medicines can come as liquids, tablets, creams, drops, or inhaled forms, but the form alone does not tell us which illness it is for.",
    sourceIds: ["nhs-child-medicines"],
  },
  {
    id: "medicine-safety",
    text: "Only use medicine supplied by a responsible adult or health professional, follow its supplied label or instructions for amount and timing, and never share medicine.",
    sourceIds: ["nhs-child-medicines", "gosh-medicine-safety"],
  },
  {
    id: "infection-definition",
    text: "Some germs can enter and affect a living thing. An illness caused this way is an infectious disease; not every germ is harmful.",
    sourceIds: ["cdc-infection-control"],
  },
  {
    id: "living-things-infection",
    text: "Infectious diseases can affect plants, animals, and people, although each disease has its own hosts and ways of spreading.",
    sourceIds: ["cdc-one-health", "fao-plant-health"],
  },
  {
    id: "general-prevention",
    text: "General hygiene, such as washing hands after animal care and cleaning shared garden tools with adult guidance, can interrupt some germ-spread routes.",
    sourceIds: ["cdc-infection-control", "cdc-one-health", "fao-plant-health"],
  },
  {
    id: "vaccination-people",
    text: "Vaccination prepares the body's defences and can prevent some diseases in people; it does not prevent every disease or treat an illness already present.",
    sourceIds: ["who-vaccination"],
  },
  {
    id: "vaccination-animals",
    text: "Animal-health professionals use vaccination to prevent and control some diseases in animals.",
    sourceIds: ["woah-animal-vaccination"],
  },
];

export const healthChallengeSources: Record<string, HealthSourceId[]> = {
  "health.trusted-care-desk": ["nhs-child-medicines", "gosh-medicine-safety"],
  "health.forms-and-claims": ["nhs-child-medicines", "who-vaccination"],
  "health.gentle-germ-trail": [
    "cdc-infection-control",
    "cdc-one-health",
    "fao-plant-health",
  ],
  "health.prevention-garden": [
    "cdc-infection-control",
    "cdc-one-health",
    "fao-plant-health",
    "who-vaccination",
    "woah-animal-vaccination",
  ],
  "health.trustworthy-research": ["who-vaccination", "woah-animal-vaccination"],
};

export interface MedicineScenario {
  id: string;
  prompt: string;
  options: Array<{ id: string; label: string; safe: boolean }>;
  safeExplanation: string;
  unsafeExplanation: string;
}

const completeMedicineRule =
  "Medicine must come from a responsible adult or health professional; follow its supplied label or instructions for amount and timing, never share it, and ask that adult, a pharmacist, or a doctor when unsure.";

export const medicineScenarios: MedicineScenario[] = [
  {
    id: "found-bottle",
    prompt:
      "A closed medicine bottle is found beside the habitat path. What is the safe action?",
    options: [
      {
        id: "tell",
        label: "Leave it alone and tell a responsible adult",
        safe: true,
      },
      { id: "open", label: "Open it to find out what it does", safe: false },
      {
        id: "share",
        label: "Offer it to a visitor who feels unwell",
        safe: false,
      },
    ],
    safeExplanation: `Leave unknown medicine alone and tell a responsible adult. ${completeMedicineRule}`,
    unsafeExplanation: `Do not touch, open, try, or share unknown medicine. Leave it alone and tell a responsible adult. ${completeMedicineRule}`,
  },
  {
    id: "adult-supplied",
    prompt:
      "A responsible adult has supplied a medicine for a fictional animal and holds the container. What should happen next?",
    options: [
      {
        id: "instructions",
        label:
          "The adult follows the supplied label and professional instructions",
        safe: true,
      },
      {
        id: "guess",
        label: "Guess an amount because the animal is small",
        safe: false,
      },
      {
        id: "copy",
        label: "Copy what another animal once received",
        safe: false,
      },
    ],
    safeExplanation: `The responsible adult follows the supplied instructions exactly and asks an animal-health professional if unsure. ${completeMedicineRule}`,
    unsafeExplanation: `Never guess, calculate, or copy an amount. A responsible adult or animal-health professional must follow the supplied instructions. ${completeMedicineRule}`,
  },
  {
    id: "friend-offers",
    prompt:
      "A friend says their medicine helped them and offers to share it. What is the safe response?",
    options: [
      {
        id: "decline",
        label: "Do not share it; tell a responsible adult",
        safe: true,
      },
      {
        id: "same",
        label: "Use it if the symptoms sound similar",
        safe: false,
      },
      { id: "half", label: "Use a smaller amount", safe: false },
    ],
    safeExplanation: `Never share medicine, even when two problems seem similar. Tell a responsible adult or health professional. ${completeMedicineRule}`,
    unsafeExplanation: `Similar symptoms do not make someone else's medicine safe. Do not share or choose an amount. ${completeMedicineRule}`,
  },
  {
    id: "unclear-label",
    prompt:
      "The supplied instructions are unclear to the responsible adult. What is the safe next step?",
    options: [
      {
        id: "ask",
        label:
          "Pause and ask a pharmacist, doctor, or animal-health professional",
        safe: true,
      },
      {
        id: "internet",
        label: "Let a child search the open web and decide",
        safe: false,
      },
      { id: "memory", label: "Rely on memory and continue", safe: false },
    ],
    safeExplanation: `Pause and ask the appropriate health professional rather than guessing. ${completeMedicineRule}`,
    unsafeExplanation: `Do not guess or ask a child to decide from unrestricted web results. The responsible adult should ask the appropriate health professional and follow supplied instructions. ${completeMedicineRule}`,
  },
];

export const infectionExamples = [
  {
    id: "plant",
    livingThing: "sunflower",
    gentleProblem: "fictional speckle spores moved on an unclean garden tool",
    prevention:
      "Ask an adult gardener to separate the tool and clean it before using it with another plant",
  },
  {
    id: "animal",
    livingThing: "hedgehog",
    gentleProblem:
      "fictional sniffle germs moved on unwashed hands after animal care",
    prevention:
      "Wash hands after animal care and ask the wildlife professional for guidance",
  },
  {
    id: "person",
    livingThing: "habitat keeper",
    gentleProblem: "fictional mist germs moved in uncovered cough droplets",
    prevention:
      "Cover coughs, wash hands, and ask a responsible adult or health professional when concerned",
  },
] as const;

export const curatedResearchCards = [
  {
    id: "who",
    sourceId: "who-vaccination" as HealthSourceId,
    organisation: "World Health Organization",
    fact: "Vaccination can prevent some diseases in people by preparing the body's defences before exposure.",
  },
  {
    id: "woah",
    sourceId: "woah-animal-vaccination" as HealthSourceId,
    organisation: "World Organisation for Animal Health",
    fact: "Animal-health professionals use vaccination to prevent and control some animal diseases.",
  },
] as const;
